import { Link } from "react-router-dom";
import { Lock, Video, Clock, ShieldCheck, Hourglass, UserRound, BadgeCheck, CircleX } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { AIOT_PATH, PORTAL, loginUrl } from "@/lib/aiot";
import { LIVE_ONLY } from "@/lib/access";
import { LiveCard } from "@/components/aiot/LiveCard";

const Btn = ({ to, href, children, testid, ghost }) => {
  const cls = `inline-flex items-center gap-2 rounded-full px-5 h-11 font-bold transition-colors ${ghost ? "border border-slate-300 bg-white text-[#0B1F3A] hover:border-[#0F766E]" : "bg-[#0F766E] text-white hover:bg-[#0B1F3A]"}`;
  return href ? <a href={href} className={cls} data-testid={testid}>{children}</a> : <Link to={to} className={cls} data-testid={testid}>{children}</Link>;
};

export const AccessBanner = ({ access }) => {
  const { L } = useLang();
  const fmt = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  let icon = <UserRound className="w-5 h-5" />, text, cta = null, tone = "bg-white border-slate-200";
  if (access.enrolled) {
    icon = <BadgeCheck className="w-5 h-5" />; tone = "bg-[#ECFDF5] border-[#A7F3D0]";
    text = L(`Enrolled · Week ${access.week} of 26 · started ${fmt(access.start_date)}`, `एनरोल्ड · 26 में से हफ़्ता ${access.week} · शुरुआत ${fmt(access.start_date)}`);
  } else if (!access.signed_in) {
    text = L("You are watching the free demos. Sign in with your RoboSpark student account to see your course access.", "आप फ़्री डेमो देख रहे हैं। कोर्स एक्सेस देखने के लिए अपने RoboSpark स्टूडेंट अकाउंट से साइन इन करें।");
    cta = <><Btn to={loginUrl(PORTAL)} testid="aiot-banner-login">{L("Student sign in", "स्टूडेंट साइन इन")}</Btn><Btn to={`${AIOT_PATH}#apply`} ghost testid="aiot-banner-apply">{L("Apply", "आवेदन करें")}</Btn></>;
  } else if (access.application === "pending" || access.application === "approved") {
    icon = <Hourglass className="w-5 h-5" />; tone = "bg-[#FFFBEB] border-[#FDE68A]";
    text = L("Your application is under review. Full classes and tests unlock as soon as the admin approves it.", "आपका आवेदन रिव्यू में है। एडमिन की मंज़ूरी मिलते ही पूरी क्लास और टेस्ट खुल जाएँगे।");
  } else if (access.application === "rejected") {
    icon = <CircleX className="w-5 h-5" />; tone = "bg-[#FEF2F2] border-[#FECACA]";
    text = L("Your application was not approved this time. Please contact us to know more.", "इस बार आपका आवेदन मंज़ूर नहीं हुआ। अधिक जानकारी के लिए हमसे संपर्क करें।");
    cta = <Btn to="/contact" ghost testid="aiot-banner-contact">{L("Contact us", "संपर्क करें")}</Btn>;
  } else {
    text = L("Apply for the diploma with this account's email to unlock all 78 classes.", "सभी 78 क्लास खोलने के लिए इसी अकाउंट के ईमेल से डिप्लोमा के लिए आवेदन करें।");
    cta = <Btn to={`${AIOT_PATH}#apply`} testid="aiot-banner-apply">{L("Apply now", "अभी आवेदन करें")}</Btn>;
  }
  return (
    <div className={`rounded-2xl border px-4 py-3 flex flex-wrap items-center gap-3 ${tone}`} data-testid="aiot-access-banner">
      <span className="text-[#0F766E]">{icon}</span>
      <div className="flex-1 min-w-[220px]">
        <p className="text-sm font-semibold text-[#0B1F3A]" data-testid="aiot-access-text">{text}</p>
        {!access.enrolled && <p className="mt-1 text-xs font-bold text-[#B45309] inline-flex items-center gap-1.5" data-testid="aiot-live-only"><Lock className="w-3.5 h-3.5" />{L(...LIVE_ONLY)}</p>}
      </div>
      {cta && <div className="flex flex-wrap gap-2">{cta}</div>}
    </div>
  );
};

export const LockedClass = ({ cls, access }) => {
  const { L } = useLang();
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-12 text-center" data-testid="aiot-class-locked">
      <span className="mx-auto w-16 h-16 rounded-2xl bg-[#0B1F3A] text-white grid place-items-center"><Lock className="w-7 h-7" /></span>
      <div className="mt-5 text-xs font-bold text-[#1D4ED8]">{L(`Class ${cls.n} · Month ${cls.month} · ${cls.minutes_lecture || cls.minutes} min`, `क्लास ${cls.n} · महीना ${cls.month} · ${cls.minutes_lecture || cls.minutes} मिनट`)}</div>
      <h2 className="mt-2 text-2xl font-extrabold text-[#0B1F3A]" data-testid="aiot-locked-title">{L(cls.title, cls.title_hi)}</h2>
      <p className="mt-2 text-sm text-slate-600" data-testid="aiot-locked-topics">{L(cls.topics.join(" · "), cls.topics_hi?.join(" · "))}</p>
      <p className="mt-6 text-sm font-semibold text-slate-700 inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#0F766E]" />{L("This class opens after your application is approved.", "यह क्लास आवेदन मंज़ूर होने के बाद खुलेगी।")}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        {!access.signed_in && <Btn to={loginUrl(PORTAL)} testid="aiot-locked-login">{L("Student sign in", "स्टूडेंट साइन इन")}</Btn>}
        <Btn to={`${AIOT_PATH}#apply`} ghost={!access.signed_in} testid="aiot-locked-apply">{L("Apply for the diploma", "डिप्लोमा के लिए आवेदन")}</Btn>
      </div>
    </div>
  );
};

const embed = (u) => {
  const m = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  const d = u.match(/drive\.google\.com\/file\/d\/([\w-]+)/);
  return d ? `https://drive.google.com/file/d/${d[1]}/preview` : null;
};

export const ClassMedia = ({ lec }) => {
  const { L } = useLang();
  const { video_url: video, live_url: live, live_at: at } = lec.media || {};
  const c = lec.class;
  if (!video && !live && lec.slides?.length) return null;
  if (!video && !live) {
    return (
      <div className="rounded-3xl border-2 border-dashed border-slate-200 bg-white p-10 text-center" data-testid="aiot-class-soon">
        <Clock className="w-10 h-10 mx-auto text-slate-400" />
        <h3 className="mt-3 text-xl font-extrabold text-[#0B1F3A]">{L("Recording coming soon", "रिकॉर्डिंग जल्द आएगी")}</h3>
        <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">{L("Your trainer will attach the recorded class or the live class link here before the class date.", "आपके ट्रेनर क्लास की तारीख से पहले यहाँ रिकॉर्डेड क्लास या लाइव क्लास का लिंक जोड़ देंगे।")}</p>
        <p className="mt-4 text-xs text-slate-500" data-testid="aiot-class-topics">{L(c.topics.join(" · "), c.topics_hi?.join(" · "))}</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {live && <LiveCard c={c} live={live} at={at} />}
      {video && (
        <div className="rounded-3xl overflow-hidden border border-slate-200 bg-black aspect-video" data-testid="aiot-class-video">
          {embed(video) ? <iframe src={embed(video)} title={c.title} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
            : <video src={video} controls className="w-full h-full" />}
        </div>
      )}
      {!video && <p className="text-sm text-slate-500 inline-flex items-center gap-2"><Video className="w-4 h-4" />{L("The recording appears here after the live class.", "लाइव क्लास के बाद रिकॉर्डिंग यहाँ आएगी।")}</p>}
    </div>
  );
};
